import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { QueryBadge } from "../../../../ui/components/QueryBadge";
import { QueryStatusBadge } from "../../../../ui/components/QueryStatusBadge";
import { QueryPriorityBadge } from "../../../../ui/components/QueryPriorityBadge";
import { Button } from "../../../../ui/components/Button";
import { IconWithBackground } from "../../../../ui/components/IconWithBackground";
import { Loader } from "../../../../ui/components/Loader";
import TableSkeletonRows from "../../../presentation/components/TableSkeletonRows";
import { Table } from "../../../../ui/components/Table";
import { Tabs } from "../../../../ui/components/Tabs";
import { TextField } from "../../../../ui/components/TextField";
import { DropdownMenu } from "../../../../ui/components/DropdownMenu";
import { FeatherChevronDown } from "@subframe/core";
import { FeatherSearch } from "@subframe/core";
import { FeatherAlertCircle } from "@subframe/core";
import { FeatherInbox } from "@subframe/core";
import { FeatherPlus } from "@subframe/core";
import { FeatherUserCheck } from "@subframe/core";
import { FeatherChevronLeft } from "@subframe/core";
import { FeatherChevronRight } from "@subframe/core";
import { useAuth } from "../../../../features/auth";
import QueryRaiseModal from "../components/QueryRaiseModal";
import QueryDetailModal from "../components/QueryDetailModal";
import { formatDate } from "../../../../utils";

// Clean Architecture: Use hooks and entities
import { useQueries } from "../hooks/useQueries";
import { useUsers } from "../hooks/useUsers";
import { QueryEntity, QueryFilters } from "../../domain/entities/QueryEntity";

const QueriesSection: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize queriesView from URL query param or default to "assigned"
  const getInitialView = (): "assigned" | "raised" => {
    const tab = searchParams.get("tab");
    return tab === "raised" ? "raised" : "assigned";
  };

  // State management
  const [isRaiseQueryModalOpen, setIsRaiseQueryModalOpen] =
    useState<boolean>(false);
  const [isQueryDetailModalOpen, setIsQueryDetailModalOpen] =
    useState<boolean>(false);
  const [selectedQueryId, setSelectedQueryId] = useState<number | undefined>();
  const [queriesView, setQueriesView] = useState<"assigned" | "raised">(
    getInitialView()
  );
  const [userSearchTerm, setUserSearchTerm] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");

  // Ref for tabs section to scroll to
  const tabsSectionRef = useRef<HTMLDivElement>(null);

  // Sync queriesView with URL query param on mount and when URL changes
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "raised" || tab === "assigned") {
      setQueriesView(tab);
    }
  }, [searchParams]);

  // Display labels for selected filters
  const [selectedSourceLabel, setSelectedSourceLabel] =
    useState<string>("Source");
  const [selectedStatusLabel, setSelectedStatusLabel] =
    useState<string>("Status");
  const [selectedUserLabel, setSelectedUserLabel] = useState<string>("");
  const [selectedPriorityLabel, setSelectedPriorityLabel] =
    useState<string>("Priority");

  // Filters state
  const [filters, setFilters] = useState<QueryFilters>({
    sort_by: "created_at",
    sort_order: "desc",
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 400);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    const normalized = debouncedSearchTerm;
    setFilters((prev) => {
      const currentSearch = prev.search ?? "";
      if (normalized === currentSearch) {
        return prev;
      }

      if (!normalized) {
        if (!prev.search) {
          return prev;
        }
        const { search, ...rest } = prev;
        return rest;
      }

      return { ...prev, search: normalized };
    });
  }, [debouncedSearchTerm]);

  // Clean Architecture: Use hooks to get domain entities
  const userId = user?.id ? user.id : undefined;
  const {
    assignedQueries,
    raisedQueries,
    counts,
    loading,
    refetch,
    assignedPagination,
    raisedPagination,
    goToNextAssignedPage,
    goToPreviousAssignedPage,
    goToNextRaisedPage,
    goToPreviousRaisedPage,
    goToAssignedPage,
    goToRaisedPage,
  } = useQueries(Number(userId), filters, queriesView);
  const { users, loading: usersLoading } = useUsers();

  // Get base queries based on view
  const baseQueries =
    queriesView === "assigned" ? assignedQueries : raisedQueries;
  const activePagination =
    queriesView === "assigned" ? assignedPagination : raisedPagination;
  const handleNextPage =
    queriesView === "assigned" ? goToNextAssignedPage : goToNextRaisedPage;
  const handlePreviousPage =
    queriesView === "assigned"
      ? goToPreviousAssignedPage
      : goToPreviousRaisedPage;
  const rangeStart =
    activePagination.total === 0 ? 0 : activePagination.offset + 1;
  const rangeEnd = Math.min(
    activePagination.offset + activePagination.limit,
    activePagination.total
  );
  const effectiveLimit = Math.max(activePagination.limit, 1);
  const totalPages = Math.ceil(activePagination.total / effectiveLimit);
  const currentPage = Math.max(
    Math.floor(activePagination.offset / effectiveLimit) + 1,
    1
  );
  const shouldShowPagination = totalPages > 1 && !loading;
  const pageNumbers =
    totalPages > 0
      ? Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          let pageNum: number;
          if (totalPages <= 5) {
            pageNum = i + 1;
          } else if (currentPage <= 3) {
            pageNum = i + 1;
          } else if (currentPage >= totalPages - 2) {
            pageNum = totalPages - 4 + i;
          } else {
            pageNum = currentPage - 2 + i;
          }

          return Math.min(Math.max(pageNum, 1), totalPages);
        })
      : [];
  const handlePageChange = (page: number) => {
    if (queriesView === "assigned") {
      goToAssignedPage(page);
    } else {
      goToRaisedPage(page);
    }
  };

  // Filter handlers
  const handleSourceFilter = (value: string, label: string) => {
    if (value === "all") {
      const { source, ...rest } = filters;
      setFilters(rest);
      setSelectedSourceLabel("Source");
    } else {
      setFilters({ ...filters, source: value });
      setSelectedSourceLabel(label);
    }
  };

  const handleStatusFilter = (value: string, label: string) => {
    if (value === "all") {
      const { status, ...rest } = filters;
      setFilters(rest);
      setSelectedStatusLabel("Status");
    } else {
      setFilters({ ...filters, status: value });
      setSelectedStatusLabel(label);
    }
  };

  const handlePriorityFilter = (value: string, label: string) => {
    if (value === "all") {
      const { priority, ...rest } = filters;
      setFilters(rest);
      setSelectedPriorityLabel("Priority");
    } else {
      setFilters({ ...filters, priority: value });
      setSelectedPriorityLabel(label);
    }
  };

  const handleUserFilter = (value: string, userName: string) => {
    if (value === "all") {
      const { raised_by, assigned_to, ...rest } = filters;
      setFilters(rest);
      setSelectedUserLabel("");
    } else {
      // In "assigned" view, filter by who raised it
      // In "raised" view, filter by who it's assigned to
      if (queriesView === "assigned") {
        const { assigned_to, ...rest } = filters;
        setFilters({ ...rest, raised_by: Number(value) });
      } else {
        const { raised_by, ...rest } = filters;
        setFilters({ ...rest, assigned_to: Number(value) });
      }
      setSelectedUserLabel(userName);
    }
  };

  const handleTabChange = (view: "assigned" | "raised") => {
    setQueriesView(view);
    // Update URL query param
    setSearchParams({ tab: view });
    // Clear user filter when switching tabs since the meaning changes
    const { raised_by, assigned_to, ...rest } = filters;
    setFilters(rest);
    setSelectedUserLabel("");
    setUserSearchTerm("");
    setSearchTerm(""); // Clear search term when switching tabs
    
    // Scroll to tabs section
    setTimeout(() => {
      tabsSectionRef.current?.scrollIntoView({ 
        behavior: "smooth", 
        block: "start" 
      });
    }, 0);
  };

  // Filter users: exclude current user and apply search
  const filteredUsers = users.filter((u) => {
    const isNotCurrentUser = u.id !== userId;
    const userName = u.userName || u.email || "";
    const matchesSearch = userName
      .toLowerCase()
      .includes(userSearchTerm.toLowerCase());
    return isNotCurrentUser && matchesSearch;
  });

  const handleReviewQuery = (query: QueryEntity) => {
    setSelectedQueryId(query.queryId);
    setIsQueryDetailModalOpen(true);
  };

  return (
    <div className="flex w-full grow shrink-0 basis-0 flex-col items-start bg-default-background overflow-auto">
      <div className="flex w-full flex-col items-start gap-4 border-b border-solid border-neutral-border bg-default-background px-8 py-6">
        <div className="flex w-full flex-wrap items-start justify-between">
          <div className="flex items-center gap-2">
            <FeatherInbox className="text-heading-2 font-heading-2 text-default-font" />
            <span className="text-heading-2 font-heading-2 text-default-font">
              Queries
            </span>
          </div>
          <Button
            icon={<FeatherPlus />}
            onClick={() => setIsRaiseQueryModalOpen(true)}
          >
            Raise Query
          </Button>
        </div>
      </div>
      <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-8 bg-default-background px-8 py-8 overflow-auto">
        <div className="flex w-full flex-col flex-wrap items-start gap-4">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Pending Queries Overview
          </span>
          <div className="flex w-full flex-wrap items-start gap-4">
            <div
              role="button"
              tabIndex={0}
              onClick={() => handleTabChange("assigned")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleTabChange("assigned");
                }
              }}
              className="flex grow shrink-0 basis-0 flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 cursor-pointer transition hover:border-brand-200 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-brand-200"
            >
              <div className="flex items-center gap-2">
                <IconWithBackground
                  variant="warning"
                  icon={<FeatherAlertCircle />}
                />
                <span className="text-body-bold font-body-bold text-subtext-color">
                  Assigned to me
                </span>
              </div>
              {loading || !counts ? (
                <Loader size="medium" />
              ) : (
                <span className="text-heading-2 font-heading-2 text-default-font">
                  {counts.assignedToMe?.total || 0}
                </span>
              )}
            </div>
            <div
              role="button"
              tabIndex={0}
              onClick={() => handleTabChange("raised")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleTabChange("raised");
                }
              }}
              className="flex grow shrink-0 basis-0 flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 cursor-pointer transition hover:border-brand-200 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-brand-200"
            >
              <div className="flex items-center gap-2">
                <IconWithBackground icon={<FeatherUserCheck />} />
                <span className="text-body-bold font-body-bold text-subtext-color">
                  Raised by me
                </span>
              </div>
              {loading || !counts ? (
                <Loader size="medium" />
              ) : (
                <span className="text-heading-2 font-heading-2 text-default-font">
                  {counts.raisedByMe?.total || 0}
                </span>
              )}
            </div>
          </div>
        </div>
        <div ref={tabsSectionRef} className="flex w-full flex-col items-start gap-4">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Queries Detailed View
          </span>
          <Tabs>
            <Tabs.Item
              active={queriesView === "assigned"}
              onClick={() => handleTabChange("assigned")}
            >
              Assigned to me
            </Tabs.Item>
            <Tabs.Item
              active={queriesView === "raised"}
              onClick={() => handleTabChange("raised")}
            >
              Raised by me
            </Tabs.Item>
          </Tabs>
          <div className="flex w-full items-center gap-4">
            <TextField
              className="h-auto grow shrink-0 basis-0"
              variant="filled"
              label=""
              helpText=""
              icon={<FeatherSearch />}
            >
              <TextField.Input
                placeholder="Search queries..."
                value={searchTerm}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchTerm(event.target.value)
                }
              />
            </TextField>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <Button
                  variant="neutral-tertiary"
                  iconRight={<FeatherChevronDown />}
                >
                  {selectedSourceLabel}
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content>
                  <DropdownMenu
                    className="flex flex-col items-start"
                    maxHeight="max-h-96"
                  >
                    <DropdownMenu.DropdownItem
                      onClick={() => handleSourceFilter("all", "Source")}
                    >
                      All Sources
                    </DropdownMenu.DropdownItem>
                    <DropdownMenu.DropdownItem
                      onClick={() => handleSourceFilter("invoice", "Invoice")}
                    >
                      Invoice
                    </DropdownMenu.DropdownItem>
                    <DropdownMenu.DropdownItem
                      onClick={() => handleSourceFilter("bill", "Bill")}
                    >
                      Bill
                    </DropdownMenu.DropdownItem>
                  </DropdownMenu>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>

            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <Button
                  variant="neutral-tertiary"
                  iconRight={<FeatherChevronDown />}
                >
                  {selectedStatusLabel}
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content>
                  <DropdownMenu
                    className="flex flex-col items-start"
                    maxHeight="max-h-96"
                  >
                    <DropdownMenu.DropdownItem
                      onClick={() => handleStatusFilter("all", "Status")}
                    >
                      All Statuses
                    </DropdownMenu.DropdownItem>
                    <DropdownMenu.DropdownItem
                      onClick={() => handleStatusFilter("open", "Open")}
                    >
                      Open
                    </DropdownMenu.DropdownItem>
                    <DropdownMenu.DropdownItem
                      onClick={() => handleStatusFilter("resolved", "Resolved")}
                    >
                      Resolved
                    </DropdownMenu.DropdownItem>
                  </DropdownMenu>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>

            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <Button
                  variant="neutral-tertiary"
                  iconRight={<FeatherChevronDown />}
                >
                  {selectedUserLabel ||
                    (queriesView === "assigned" ? "Raised By" : "Assigned To")}
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content>
                  <DropdownMenu
                    className="flex flex-col items-start"
                    maxHeight="max-h-96"
                  >
                    <div className="px-2 py-2 w-full">
                      <TextField
                        variant="filled"
                        label=""
                        helpText=""
                        icon={<FeatherSearch />}
                      >
                        <TextField.Input
                          placeholder="Search users..."
                          value={userSearchTerm}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setUserSearchTerm(e.target.value)
                          }
                        />
                      </TextField>
                    </div>
                    <DropdownMenu.DropdownItem
                      onClick={() =>
                        handleUserFilter(
                          "all",
                          queriesView === "assigned"
                            ? "Raised By"
                            : "Assigned To"
                        )
                      }
                    >
                      {queriesView === "assigned"
                        ? "All Users"
                        : "All Assignees"}
                    </DropdownMenu.DropdownItem>
                    {!usersLoading &&
                      filteredUsers.map((u) => {
                        const userName =
                          u.user_name ||
                          u.userName ||
                          u.email ||
                          `User ${u.id}`;
                        return (
                          <DropdownMenu.DropdownItem
                            key={u.id}
                            onClick={() =>
                              handleUserFilter(u.id.toString(), userName)
                            }
                          >
                            {userName}
                          </DropdownMenu.DropdownItem>
                        );
                      })}
                    {!usersLoading &&
                      filteredUsers.length === 0 &&
                      userSearchTerm && (
                        <div className="px-3 py-2 text-body text-neutral-500">
                          No users found
                        </div>
                      )}
                  </DropdownMenu>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>

            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <Button
                  variant="neutral-tertiary"
                  iconRight={<FeatherChevronDown />}
                >
                  {selectedPriorityLabel}
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content>
                  <DropdownMenu
                    className="flex flex-col items-start"
                    maxHeight="max-h-96"
                  >
                    <DropdownMenu.DropdownItem
                      onClick={() => handlePriorityFilter("all", "Priority")}
                    >
                      All Priorities
                    </DropdownMenu.DropdownItem>
                    <DropdownMenu.DropdownItem
                      onClick={() => handlePriorityFilter("high", "High")}
                    >
                      High
                    </DropdownMenu.DropdownItem>
                    <DropdownMenu.DropdownItem
                      onClick={() => handlePriorityFilter("medium", "Medium")}
                    >
                      Medium
                    </DropdownMenu.DropdownItem>
                    <DropdownMenu.DropdownItem
                      onClick={() => handlePriorityFilter("low", "Low")}
                    >
                      Low
                    </DropdownMenu.DropdownItem>
                  </DropdownMenu>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>
          <Table
            header={
              <Table.HeaderRow>
                <Table.HeaderCell>Query ID</Table.HeaderCell>
                <Table.HeaderCell>Source</Table.HeaderCell>
                <Table.HeaderCell>Description</Table.HeaderCell>
                <Table.HeaderCell>
                  {queriesView === "assigned" ? "Raised By" : "Assigned To"}
                </Table.HeaderCell>
                <Table.HeaderCell>Date</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Priority</Table.HeaderCell>
                <Table.HeaderCell />
              </Table.HeaderRow>
            }
          >
            {loading ? (
              <TableSkeletonRows rowCount={5} columnCount={8} />
            ) : baseQueries.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={8}>
                  <div className="flex justify-center py-4">
                    <span className="text-body text-neutral-500">
                      No queries found
                    </span>
                  </div>
                </Table.Cell>
              </Table.Row>
            ) : (
              baseQueries.map((r) => (
                <Table.Row key={r.queryId}>
                  <Table.Cell>
                    <span className="text-body-bold">{r.queryId}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <QueryBadge source={r.source}>
                      {r.source.toUpperCase()}
                    </QueryBadge>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-body text-neutral-500 max-w-md truncate block">
                      {r.description}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    {queriesView === "assigned"
                      ? r.raisedBy.userName
                      : r.assignedTo?.userName || "Unassigned"}
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-body text-neutral-500">
                      {formatDate(r.createdAt.toISOString())}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <QueryStatusBadge status={r.status}>
                      {r.status.toUpperCase()}
                    </QueryStatusBadge>
                  </Table.Cell>
                  <Table.Cell>
                    <QueryPriorityBadge
                      priority={r.priority as "low" | "medium" | "high"}
                    >
                      {r.priority.toUpperCase()}
                    </QueryPriorityBadge>
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      variant="neutral-tertiary"
                      size="small"
                      onClick={() => handleReviewQuery(r)}
                    >
                      Review
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table>
          {shouldShowPagination && (
            <div className="flex w-full items-center justify-between gap-4 border-t border-neutral-border px-4 py-4">
              <div className="flex items-center gap-2">
                <span className="text-body text-neutral-500">
                  Showing {rangeStart} to {rangeEnd} of {activePagination.total}{" "}
                  results
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="neutral-tertiary"
                  size="small"
                  onClick={handlePreviousPage}
                  disabled={loading || currentPage === 1}
                  icon={<FeatherChevronLeft />}
                >
                  Previous
                </Button>

                <div className="flex items-center gap-1">
                  {pageNumbers.map((pageNum) => (
                    <Button
                      key={pageNum}
                      variant={
                        currentPage === pageNum
                          ? "brand-primary"
                          : "neutral-tertiary"
                      }
                      size="small"
                      onClick={() => handlePageChange(pageNum)}
                      className="min-w-[32px]"
                    >
                      {pageNum}
                    </Button>
                  ))}
                </div>

                <Button
                  variant="neutral-tertiary"
                  size="small"
                  onClick={handleNextPage}
                  disabled={loading || !activePagination.hasMore}
                  icon={<FeatherChevronRight />}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      <QueryRaiseModal
        open={isRaiseQueryModalOpen}
        onOpenChange={(open) => {
          setIsRaiseQueryModalOpen(open);
          if (!open) refetch();
        }}
      />
      <QueryDetailModal
        open={isQueryDetailModalOpen}
        onOpenChange={(open) => {
          setIsQueryDetailModalOpen(open);
        }}
        queryId={selectedQueryId}
        onQueryResolved={refetch}
      />
    </div>
  );
};

export default QueriesSection;
